// import muslifinLogo from "figma:asset/0beb4b118de10ed84c607831432f31a0e98fdec9.png";
import { Linkedin, Mail, Phone, Copy } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { ApplyNowForm } from "./ApplyNowForm";
import { toast } from "sonner";

interface FooterProps {
  setCurrentPage?: (page: string) => void;
}

export function Footer({ setCurrentPage }: FooterProps) {
  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard!`);
    } catch (err) {
      toast.error(`Failed to copy ${type.toLowerCase()}`);
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-12 lg:gap-16 xl:gap-20 items-start">
          {/* Left side - Logo and about */}
          <div className="lg:pr-8">
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <div className="flex items-center">
                  <img 
                    src="/assets/muslifin-logo-blue.png.png" 
                    alt="MusliFin" 
                    className="h-10 w-auto"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      const span = img.nextElementSibling as HTMLElement;
                      img.style.display = 'none';
                      span.style.display = 'block';
                    }}
                  />
                  <span 
                    className="h-10 text-2xl font-bold text-white hidden"
                    style={{ display: 'none' }}
                  >
                    MusliFin
                  </span>
                </div>
              </div>
              <div className="text-gray-400 mb-3">
                Your Muslim Financing Partner
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed text-base">
              Building Shariah-compliant financial infrastructure for the modern Muslim community. 
              Starting with auto finance and scaling to serve all financial needs.
            </p>
          </div>

          {/* Middle - Quick Links (left-justified) */}
          <div className="lg:pl-4 lg:pr-12">
            <h4 className="mb-4 text-xl">Quick Links</h4>
            <div className="grid grid-cols-2 gap-6">
              {/* First sub-column - Navigation & Contact */}
              <div className="space-y-3">
                <div>
                  <button 
                    onClick={() => setCurrentPage?.('dealerships')}
                    className="text-gray-400 hover:text-white transition-colors py-1"
                  >
                    For Dealerships
                  </button>
                </div>
                <div>
                  <button 
                    onClick={() => setCurrentPage?.('investors')}
                    className="text-gray-400 hover:text-white transition-colors py-1"
                  >
                    For Investors
                  </button>
                </div>
                <div>
                  <ContactForm>
                    <button className="text-gray-400 hover:text-white transition-colors py-1">
                      Contact Us
                    </button>
                  </ContactForm>
                </div>
                <div>
                  <ContactForm>
                    <button className="text-gray-400 hover:text-white transition-colors py-1">
                      Contact Form
                    </button>
                  </ContactForm>
                </div>
              </div>
              
              {/* Second sub-column - Forms & Legal */}
              <div className="space-y-3">
                <div>
                  <ApplyNowForm 
                    asModal={true}
                    triggerButton={
                      <button className="text-gray-400 hover:text-white transition-colors py-1">
                        Express Interest
                      </button>
                    }
                  />
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors block py-1">
                    Terms of Service
                  </a>
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors block py-1">
                    Privacy Policy
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Contact Information (pushed further right) */}
          <div className="lg:pl-12">
            <h4 className="mb-4 text-xl">Contact Us</h4>
            <div className="space-y-3">
              <div className="group">
                <button 
                  onClick={() => copyToClipboard('contact@muslifin.com', 'Email')}
                  className="flex items-center space-x-4 text-gray-400 hover:text-white transition-colors w-full text-left group py-1"
                >
                  <Mail className="w-5 h-5 text-[#004aad] flex-shrink-0" />
                  <span className="flex-1">contact@muslifin.com</span>
                  <Copy className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              </div>
              <div className="group">
                <button 
                  onClick={() => copyToClipboard('+1 (437) 887-1015', 'Phone')}
                  className="flex items-center space-x-4 text-gray-400 hover:text-white transition-colors w-full text-left group py-1"
                >
                  <Phone className="w-5 h-5 text-[#004aad] flex-shrink-0" />
                  <span className="flex-1">+1 (437) 887-1015</span>
                  <Copy className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              </div>
              <div>
                <a 
                  href="https://www.linkedin.com/company/muslifin" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 text-gray-400 hover:text-white transition-colors py-1"
                >
                  <Linkedin className="w-5 h-5 text-[#004aad] flex-shrink-0" />
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom line */}
        <div className="border-t border-gray-800 mt-8 pt-6 text-center">
          <p className="text-gray-400">
            Built with care by MusliFin
          </p>
        </div>
      </div>
    </footer>
  );
}